import type { ReactNode } from 'react';
import { Link, NavLink, useLocation } from 'react-router';
import { useAuth } from '../lib/auth';
import { ACCENTS, accentForPath } from './accent';
import { BrandLogo } from './BrandLogo';
import { HouseholdSwitcher } from './HouseholdSwitcher';
import { GearIcon, ShieldIcon } from './icons';
import { GroceriesIcon, HomeIcon, PantryIcon, PlanIcon, RecipesIcon } from './NavIcons';

const NAV = [
  { to: '/', label: 'Home', Icon: HomeIcon },
  { to: '/recipes', label: 'Recipes', Icon: RecipesIcon },
  { to: '/plan', label: 'Plan', Icon: PlanIcon },
  { to: '/pantry', label: 'Pantry', Icon: PantryIcon },
  { to: '/groceries', label: 'Groceries', Icon: GroceriesIcon },
] as const;

/** Thin teal → green → orange stripe: the brand card's three colours in one line. */
function BrandStripe() {
  return (
    <div
      aria-hidden
      className="h-1 bg-[linear-gradient(90deg,var(--color-brand)_0%,var(--color-brand)_40%,var(--color-accent)_40%,var(--color-accent)_75%,var(--color-orange)_75%)]"
    />
  );
}

function Avatar({ name, className = 'size-10' }: { name: string; className?: string }) {
  return (
    <span
      aria-hidden
      className={`flex shrink-0 items-center justify-center rounded-full bg-accent font-semibold text-ink ring-2 ring-surface ${className}`}
    >
      {name.slice(0, 1).toUpperCase()}
    </span>
  );
}

/** Desktop (lg+) left rail on deep teal; white text only on teal, ink on green. */
function Sidebar() {
  const { auth } = useAuth();
  const authed = auth.status === 'authed' ? auth : null;
  const itemClass = ({ isActive }: { isActive: boolean }) =>
    `group flex min-h-12 items-center gap-3 rounded-xl px-3 text-[0.95rem] font-semibold transition-colors ${
      isActive ? 'bg-accent text-ink shadow-sm' : 'text-white/85 hover:bg-white/10 hover:text-white'
    }`;
  return (
    <aside className="on-teal fixed inset-y-0 left-0 z-20 hidden w-64 flex-col bg-brand text-white lg:flex">
      <BrandStripe />
      <NavLink
        to="/"
        aria-label="FewerBags home"
        className="mx-4 mt-5 mb-6 flex items-center gap-3 rounded-xl px-1 py-1"
      >
        <span className="flex size-12 items-center justify-center rounded-2xl bg-cream shadow-sm">
          <BrandLogo size={30} wordmark={false} />
        </span>
        <span className="text-xl font-bold tracking-tight text-white">FewerBags</span>
      </NavLink>
      <nav aria-label="Primary" className="flex-1 px-3">
        <ul className="flex flex-col gap-1">
          {NAV.map(({ to, label, Icon }) => (
            <li key={to}>
              <NavLink to={to} end={to === '/'} className={itemClass}>
                <span className="flex size-9 items-center justify-center rounded-lg bg-cream">
                  <Icon className="size-6" />
                </span>
                {label}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
      {authed ? (
        <div className="flex flex-col gap-1 border-t border-white/15 px-3 py-4">
          {authed.user.isAdmin ? (
            <NavLink to="/admin" className={itemClass}>
              <span className="flex size-9 items-center justify-center">
                <ShieldIcon />
              </span>
              Admin
            </NavLink>
          ) : null}
          <NavLink to="/settings" className={itemClass}>
            <span className="flex size-9 items-center justify-center">
              <GearIcon />
            </span>
            Settings
          </NavLink>
          <div className="mt-2 flex items-center gap-3 rounded-xl bg-white/10 px-3 py-2.5">
            <Avatar name={authed.user.displayName} className="size-9 ring-brand" />
            <div className="min-w-0 flex-1 text-sm">
              <p className="truncate font-semibold text-white">{authed.user.displayName}</p>
              {authed.household ? (
                <p className="truncate text-xs text-white/75">{authed.household.name}</p>
              ) : null}
            </div>
          </div>
          <div className="[&_select]:w-full [&_select]:max-w-none [&_label]:mt-1">
            <HouseholdSwitcher />
          </div>
        </div>
      ) : null}
    </aside>
  );
}

export function AppShell({
  children,
  title,
  width = 'default',
}: {
  children: ReactNode;
  title?: string;
  /** `wide` lets grids and boards use the full desktop width (lg+). */
  width?: 'default' | 'wide';
}) {
  const { auth } = useAuth();
  const { pathname } = useLocation();
  const accent = ACCENTS[accentForPath(pathname)];
  const maxW = width === 'wide' ? 'max-w-3xl lg:max-w-6xl' : 'max-w-3xl';
  return (
    <div className="flex min-h-dvh flex-col lg:pl-64">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-50 focus:rounded-lg focus:bg-brand focus:px-4 focus:py-2 focus:text-white"
      >
        Skip to content
      </a>
      <Sidebar />
      <header className="sticky top-0 z-10 border-b border-line bg-surface/90 shadow-[0_1px_0_rgb(28_95_83/0.04)] backdrop-blur lg:hidden">
        <BrandStripe />
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-3 px-4 py-2">
          <NavLink
            to="/"
            aria-label="FewerBags home"
            className="flex min-h-11 items-center rounded-lg"
          >
            <BrandLogo size={32} />
          </NavLink>
          <div className="flex items-center gap-2">
            {auth.status === 'authed' ? (
              <>
                <HouseholdSwitcher />
                {auth.user.isAdmin ? (
                  <NavLink
                    to="/admin"
                    className="inline-flex min-h-11 items-center rounded-lg px-2 text-sm font-medium text-brand hover:bg-brand-soft"
                  >
                    Admin
                  </NavLink>
                ) : null}
                <NavLink
                  to="/settings"
                  aria-label="Settings"
                  className="flex size-11 items-center justify-center rounded-full"
                >
                  <Avatar name={auth.user.displayName} />
                </NavLink>
              </>
            ) : auth.status === 'anon' ? (
              <Link
                to="/login"
                className="inline-flex min-h-11 items-center px-2 text-sm font-medium text-brand underline-offset-2 hover:underline"
              >
                Sign in
              </Link>
            ) : null}
          </div>
        </div>
      </header>
      <main
        id="main"
        tabIndex={-1}
        className={`mx-auto w-full flex-1 px-4 py-6 lg:px-8 lg:py-10 ${maxW}`}
      >
        {title ? (
          <div className="mb-5 flex items-center gap-3">
            <span aria-hidden className={`h-8 w-1.5 shrink-0 rounded-full ${accent.bar}`} />
            <h1 className="text-2xl sm:text-3xl">{title}</h1>
          </div>
        ) : null}
        {children}
      </main>
      <nav
        aria-label="Primary"
        className="sticky bottom-0 z-10 border-t border-line bg-surface/95 pb-[env(safe-area-inset-bottom)] shadow-[0_-4px_16px_-8px_rgb(28_95_83/0.18)] backdrop-blur lg:hidden"
      >
        <ul className="mx-auto flex max-w-3xl justify-around px-1">
          {NAV.map(({ to, label, Icon }) => (
            <li key={to} className="flex-1">
              <NavLink
                to={to}
                end={to === '/'}
                className={({ isActive }) =>
                  `group flex min-h-16 flex-col items-center justify-center gap-0.5 text-xs ${
                    isActive ? 'font-semibold text-brand' : 'font-medium text-muted'
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <span
                      className={`flex h-8 w-14 items-center justify-center rounded-full transition-colors ${
                        isActive ? 'bg-accent-soft ring-1 ring-accent' : 'group-hover:bg-brand-soft'
                      }`}
                    >
                      <Icon className="size-6" />
                    </span>
                    {label}
                  </>
                )}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
}
